import type {
  AdvisorDraft,
  AdvisorDraftItem,
  AdvisorDraftLeg,
  AdvisorProduct,
} from './types';
import {
  isDaySuggestLogisticsNoise,
  pickFromPreRankedCatalog,
  rankDayCatalog,
} from '@travel-planner/recommendation';
import { orderDayItems, usualStartTime } from './add-item';
import { advisorRegionKey } from './region-layout';
import { itineraryProductIds } from './v3-results';

// Mini Planner v3 per-day Generate: the recommender's picks for one day,
// offered inline to accept or skip instead of filling the day. When sold-
// history is thin, the catalogue tops up via `@travel-planner/recommendation`
// (sold mass + brief moods + kind mix + subcategory diversity + proximity)
// so the panel always offers at least DAY_SUGGESTION_LIMIT picks when stock
// allows.

/** Suggestions offered per day. */
export const DAY_SUGGESTION_LIMIT = 3;

/** Suggestions waiting on each day, by day id. */
export type DaySuggestions = Record<string, AdvisorDraftItem[]>;

const nextId = (prefix: string): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const matchingLeg = (
  leg: AdvisorDraftLeg,
  incoming: AdvisorDraft
): AdvisorDraftLeg | undefined =>
  incoming.legs.find(
    (entry) =>
      advisorRegionKey(entry.regionTitle) === advisorRegionKey(leg.regionTitle)
  ) ?? incoming.legs[0];

/** A catalogue row as a day suggestion (price/map filled later by lookup). */
export const catalogProductToSuggestion = (
  product: AdvisorProduct
): AdvisorDraftItem => ({
  id: nextId('item'),
  productId: product.id,
  title: product.title,
  kind: product.kind,
  clientPrice: product.clientPrice,
  childPrice: product.childPrice ?? null,
  currency: product.currency,
  subtitle: product.subtitle,
  thumbnail: product.thumbnail,
  origin: 'suggested',
  startTime: usualStartTime(product),
  location: product.location ?? null,
  pricingType: product.pricingType ?? null,
  durationMinutes: product.durationMinutes ?? null,
  avgRoomNight: product.avgRoomNight ?? null,
  detailsPending: true,
});

/**
 * Up to three picks for the day from a Generate preview: the preview's day at
 * the same position first, then its other days. No hotels (the chapter has its
 * base hotel), nothing already in the trip and nothing waiting on another
 * day. Kinds the day lacks come first; otherwise the recommender's order is
 * kept.
 */
export const pickDaySuggestions = (
  draft: AdvisorDraft,
  incoming: AdvisorDraft,
  legId: string,
  dayId: string,
  waiting: DaySuggestions = {}
): AdvisorDraftItem[] => {
  const leg = draft.legs.find((entry) => entry.id === legId);
  const index = leg?.days.findIndex((day) => day.id === dayId) ?? -1;
  const day = leg?.days[index];
  const source = leg ? matchingLeg(leg, incoming) : undefined;
  if (!day || !source) return [];
  const sameDay = source.days[index] ?? source.days[source.days.length - 1];
  const inTrip = itineraryProductIds(draft);
  // Dismissed anywhere: the catalogue's last resort brings back other days'.
  const dismissed = new Set(
    draft.legs.flatMap((entry) =>
      entry.days.flatMap((d) => d.dismissedProductIds ?? [])
    )
  );
  // Suggestions waiting on other days stay unique across the trip.
  const waitingElsewhere = new Set(
    Object.entries(waiting).flatMap(([id, items]) =>
      id === dayId ? [] : items.map((item) => item.productId)
    )
  );
  const seen = new Set<string>();
  const pool: AdvisorDraftItem[] = [];
  for (const sourceDay of [sameDay, ...source.days]) {
    for (const item of sourceDay?.items ?? []) {
      // Hotels stay on the chapter base; transfers are logistics, not picks.
      if (item.kind === 'hotel' || item.slot === 'transfer') continue;
      // Closed / placeholder / logistics rows are not day picks.
      if (isDaySuggestLogisticsNoise(item.title)) continue;
      if (inTrip.has(item.productId) || seen.has(item.productId)) continue;
      if (dismissed.has(item.productId)) continue;
      if (waitingElsewhere.has(item.productId)) continue;
      seen.add(item.productId);
      pool.push(item);
    }
  }
  const onDay = new Set(day.items.map((item) => item.kind));
  return [
    ...pool.filter((item) => !onDay.has(item.kind)),
    ...pool.filter((item) => onDay.has(item.kind)),
  ]
    .slice(0, DAY_SUGGESTION_LIMIT)
    .map((item) => ({ ...item, origin: 'suggested' as const }));
};

const hotelPointOnDay = (
  day: { items: readonly AdvisorDraftItem[] },
  leg: AdvisorDraftLeg
): { lat: number; lng: number } | null => {
  for (const item of [...day.items, ...leg.days.flatMap((d) => d.items)]) {
    if (item.kind === 'hotel' && item.location) return item.location;
  }
  return null;
};

/**
 * Fill remaining suggestion slots from the catalogue.
 *
 * - **preRanked** rows (from `suggestCatalog`): order preserved, no second score.
 * - raw catalogue (tests / legacy): full `rankDayCatalog` on the client.
 */
export const topUpDaySuggestions = (
  draft: AdvisorDraft,
  legId: string,
  dayId: string,
  current: readonly AdvisorDraftItem[],
  catalog: readonly AdvisorProduct[],
  briefMoods: readonly string[] = []
): AdvisorDraftItem[] => {
  if (current.length >= DAY_SUGGESTION_LIMIT) {
    return current.slice(0, DAY_SUGGESTION_LIMIT);
  }
  const leg = draft.legs.find((entry) => entry.id === legId);
  const day = leg?.days.find((entry) => entry.id === dayId);
  if (!day || !leg) return current.slice(0, DAY_SUGGESTION_LIMIT);

  const inTrip = itineraryProductIds(draft);
  const seen = new Set(current.map((item) => item.productId));
  const onDay = new Set([
    ...day.items.map((item) => item.kind),
    ...current.map((item) => item.kind),
  ]);
  const need = DAY_SUGGESTION_LIMIT - current.length;
  const excludeIds = new Set([
    ...inTrip,
    ...seen,
    ...(day.dismissedProductIds ?? []),
  ]);
  const asCatalog = catalog.map((product) => ({
    id: product.id,
    title: product.title,
    kind: product.kind,
    tags: product.tags,
    subcategory: product.subcategory ?? null,
    city: product.subtitle,
    soldTrips: product.soldTrips ?? 0,
    cancelledTrips: product.cancelledTrips ?? 0,
    location: product.location ?? null,
  }));

  const serverRanked =
    catalog.length > 0 &&
    catalog.every((product) => product.preRanked === true);

  const ranked = serverRanked
    ? pickFromPreRankedCatalog(asCatalog, {
        limit: need,
        excludeIds,
        kindsOnDay: onDay,
      })
    : rankDayCatalog({
        products: asCatalog,
        briefMoods,
        dayCity:
          day.items.find((item) => item.kind === 'hotel')?.subtitle ?? null,
        hotelPoint: hotelPointOnDay(day, leg),
        excludeIds,
        kindsOnDay: onDay,
        limit: need,
      });

  const extra = ranked.map((row) => {
    const product =
      catalog.find((entry) => entry.id === row.id) ??
      ({
        id: row.id,
        title: row.title,
        subtitle: row.city ?? null,
        tags: [...(row.tags ?? [])],
        clientPrice: null,
        currency: null,
        kind: row.kind as AdvisorProduct['kind'],
        regionId: null,
        regionTitle: '',
        thumbnail: null,
        location: row.location ?? null,
        subcategory: row.subcategory ?? null,
        soldTrips: row.soldTrips,
        cancelledTrips: row.cancelledTrips,
      } satisfies AdvisorProduct);
    return catalogProductToSuggestion(product);
  });

  return [...current, ...extra].slice(0, DAY_SUGGESTION_LIMIT);
};

/** Builder picks first; catalogue tops up to DAY_SUGGESTION_LIMIT. */
export const buildDaySuggestions = (
  draft: AdvisorDraft,
  incoming: AdvisorDraft,
  legId: string,
  dayId: string,
  catalog: readonly AdvisorProduct[] = [],
  briefMoods: readonly string[] = [],
  waiting: DaySuggestions = {}
): AdvisorDraftItem[] =>
  topUpDaySuggestions(
    draft,
    legId,
    dayId,
    pickDaySuggestions(draft, incoming, legId, dayId, waiting),
    catalog,
    briefMoods
  );

/** Accepting puts the suggestion on its day as the agent's own item. */
export const acceptDaySuggestion = (
  draft: AdvisorDraft,
  dayId: string,
  item: AdvisorDraftItem
): AdvisorDraft => ({
  ...draft,
  legs: draft.legs.map((leg) => ({
    ...leg,
    days: leg.days.map((day) =>
      day.id === dayId
        ? {
            ...day,
            items: orderDayItems([
              ...day.items,
              { ...item, id: nextId('item'), origin: 'manual' },
            ]),
          }
        : day
    ),
  })),
});

/**
 * Drops one suggestion; a day left with none loses its entry, so an empty
 * list only ever means "nothing new to suggest".
 */
export const withoutSuggestion = (
  suggestions: DaySuggestions,
  dayId: string,
  itemId: string
): DaySuggestions => {
  const { [dayId]: current = [], ...rest } = suggestions;
  const left = current.filter((item) => item.id !== itemId);
  return left.length > 0 ? { ...rest, [dayId]: left } : rest;
};

/** Removes a day's suggestions (dismissed, or the day regenerated). */
export const withoutDay = (
  suggestions: DaySuggestions,
  dayId: string
): DaySuggestions => {
  const { [dayId]: _, ...rest } = suggestions;
  return rest;
};

/**
 * Records products the agent skipped or dismissed on a day. Returns the same
 * draft when nothing new is recorded.
 */
export const dismissOnDay = (
  draft: AdvisorDraft,
  dayId: string,
  productIds: readonly string[]
): AdvisorDraft => {
  let changed = false;
  const legs = draft.legs.map((leg) => {
    if (!leg.days.some((day) => day.id === dayId)) return leg;
    return {
      ...leg,
      days: leg.days.map((day) => {
        if (day.id !== dayId) return day;
        const known = day.dismissedProductIds ?? [];
        const added = [...new Set(productIds)].filter(
          (id) => id && !known.includes(id)
        );
        if (added.length === 0) return day;
        changed = true;
        return { ...day, dismissedProductIds: [...known, ...added] };
      }),
    };
  });
  return changed ? { ...draft, legs } : draft;
};

/** `suggestCatalog` accepts at most this many excluded product ids. */
export const MAX_SUGGEST_EXCLUDES = 200;

/**
 * What a day's catalogue top-up leaves out. `excludeIds` is never offered;
 * `dismissedElsewhere` (skipped on other days) only as a last resort.
 * Ordered by importance so the server cap keeps this day's dismissals.
 */
export const daySuggestExclusions = (
  draft: AdvisorDraft,
  waiting: DaySuggestions,
  dayId: string,
  builderPicks: readonly AdvisorDraftItem[]
): { excludeIds: string[]; dismissedElsewhere: string[] } => {
  const days = draft.legs.flatMap((leg) => leg.days);
  const excludeIds = [
    ...new Set([
      ...(days.find((day) => day.id === dayId)?.dismissedProductIds ?? []),
      ...itineraryProductIds(draft),
      ...builderPicks.map((item) => item.productId),
      // Suggestions waiting on other days stay unique across the trip.
      ...Object.entries(waiting).flatMap(([id, items]) =>
        id === dayId ? [] : items.map((item) => item.productId)
      ),
    ]),
  ];
  const excluded = new Set(excludeIds);
  const dismissedElsewhere = [
    ...new Set(
      days
        .filter((day) => day.id !== dayId)
        .flatMap((day) => day.dismissedProductIds ?? [])
    ),
  ].filter((id) => !excluded.has(id));
  return { excludeIds, dismissedElsewhere };
};

/** One catalogue request's filters; later attempts are looser. */
export type DayCatalogAttempt = {
  regions?: string[];
  kinds?: string[];
  moods?: string[];
};

/**
 * Fills `need` catalogue picks, loosening filters attempt by attempt.
 * Products dismissed on other days are held back from every attempt and
 * allowed again only in one final loosest request, when nothing else fits.
 */
export const collectDayCatalogPicks = async ({
  need,
  attempts,
  excludeIds,
  dismissedElsewhere,
  fetchPicks,
  isCancelled = () => false,
}: {
  need: number;
  attempts: readonly DayCatalogAttempt[];
  excludeIds: readonly string[];
  dismissedElsewhere: readonly string[];
  fetchPicks: (
    request: DayCatalogAttempt & { exclude: string[]; limit: number }
  ) => Promise<AdvisorProduct[]>;
  isCancelled?: () => boolean;
}): Promise<AdvisorProduct[]> => {
  const collected: AdvisorProduct[] = [];
  const take = async (
    attempt: DayCatalogAttempt,
    held: readonly string[]
  ): Promise<void> => {
    const still = need - collected.length;
    if (still <= 0 || isCancelled()) return;
    const exclude = [
      ...new Set([...held, ...collected.map((product) => product.id)]),
    ].slice(0, MAX_SUGGEST_EXCLUDES);
    const batch = await fetchPicks({ ...attempt, exclude, limit: still });
    // The server cap can cut ids; hold them back here too.
    const skip = new Set([...held, ...collected.map((product) => product.id)]);
    for (const row of batch) {
      if (skip.has(row.id)) continue;
      skip.add(row.id);
      collected.push(row);
      if (collected.length >= need) break;
    }
  };
  for (const attempt of attempts) {
    await take(attempt, [...excludeIds, ...dismissedElsewhere]);
  }
  const loosest = attempts[attempts.length - 1];
  if (loosest && dismissedElsewhere.length > 0) {
    await take(loosest, excludeIds);
  }
  return collected.slice(0, need);
};
